import axios from 'axios';
import { config } from '../config';

// Export all types and services from maintenance module
export * from './maintenance';

// Create and configure axios instance
export const api = axios.create({
  baseURL: config.api.baseURL,
  withCredentials: true,
});

/**
 * EventEmitter global para orquestrar logout em caso de 401 (sessão expirada).
 * Usado pelo interceptor (módulo plano, sem hooks) para avisar o AuthContext.
 * AuthContext escuta este evento e executa logout() sem full reload da página.
 */
const _authLogoutEventTarget = new EventTarget();
export const authLogoutEvent: EventTarget = _authLogoutEventTarget;
export const AUTH_LOGOUT_EVENT_NAME = 'auth:logout';

let isRefreshingSession = false;
let refreshPromise: Promise<void> | null = null;

const refreshSession = async () => {
  if (!refreshPromise) {
    isRefreshingSession = true;
    refreshPromise = axios
      .post(
        `${config.api.baseURL}/auth/refresh`,
        {},
        {
          withCredentials: true,
        },
      )
      .then(() => undefined)
      .finally(() => {
        isRefreshingSession = false;
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

export const getApiErrorStatus = (error: unknown): number | undefined => {
  if (axios.isAxiosError(error)) {
    return error.response?.status;
  }

  return undefined;
};

export const getApiErrorMessage = (
  error: unknown,
  fallbackMessage = 'Erro ao processar a requisição.',
): string => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const responseMessage = error.response?.data?.message;

    if (typeof responseMessage === 'string' && responseMessage.trim()) {
      return responseMessage;
    }

    if (Array.isArray(responseMessage) && responseMessage.length > 0) {
      return responseMessage.join(', ');
    }

    if (status === 401) {
      return 'Sua sessão expirou ou não foi autenticada.';
    }

    if (status === 403) {
      return 'Você não tem permissão para acessar este recurso.';
    }

    if (status && status >= 500) {
      return 'O servidor encontrou um erro ao processar a solicitação.';
    }

    if (error.message) {
      return error.message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
};

// Handle 401 Unauthorized responses and unwrap standardized API responses
api.interceptors.response.use(
  (response) => {
    // Unwrap { status, data, message } response structure if present
    if (
      response.data && 
      !Array.isArray(response.data) && 
      response.data.status === 'success' && 
      response.data.data !== undefined
    ) {
      response.data = response.data.data;
    }
    return response;
  },
  async (error) => {
    if (!axios.isAxiosError(error)) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    const requestUrl = originalRequest?.url ?? '';
    const isAuthRoute =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/refresh') ||
      requestUrl.includes('/auth/logout');
    const canRetry =
      error.response?.status === 401 &&
      originalRequest &&
      !isAuthRoute &&
      !(originalRequest as typeof originalRequest & { _retry?: boolean })._retry;

    if (canRetry) {
      try {
        (originalRequest as typeof originalRequest & { _retry?: boolean })._retry =
          true;
        await refreshSession();
        return api(originalRequest);
      } catch {
        // Fall through to cleanup and redirect below.
      }
    }

    if (error.response?.status === 401 && !isRefreshingSession) {
      localStorage.removeItem(config.auth.storageKeys.token);
      localStorage.removeItem(config.auth.storageKeys.user);

      if (!window.location.pathname.includes('/login')) {
        // Em vez de window.location.href (full reload da página),
        // disparamos um evento para o AuthContext executar logout() + Navigate
        // do React Router — preserva estado, mais rápido, sem flicker.
        try {
          _authLogoutEventTarget.dispatchEvent(new Event(AUTH_LOGOUT_EVENT_NAME));
        } catch {
          // Fallback extremo se algo der errado no dispatch: redirect hard.
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Re-export other API modules
export * from './vehicles';
export * from './provedor';

export default api;
